// Phase 6 / Launch-readiness refactor:
// Centralizes the in-run upgrade chooser, VIP reroll, chooser meter scaling,
// run-power-gain accounting, and the daily-crate Lucky Run consume flow.
//
// Pure hook — no JSX. Game.jsx wires the returned state into <RewardModal />
// and reads runPowers/setRunPowers from props.
//
// Behavior is preserved exactly from the previous inline implementation in
// Game.jsx (verified by side-by-side review). No gameplay changes.
import { useCallback, useEffect, useRef, useState } from "react";
import { RUN_POWERS } from "./upgrades";
import { playClick, playEpicReveal } from "./sound";
import { consumeLuckyRun } from "./authService";

function rollRarities(count) {
  return Array.from({ length: count }, () => {
    const r = Math.random();
    if (r < 0.1) return "epic";
    if (r < 0.4) return "rare";
    return "common";
  });
}

function buildPool(runPowers) {
  return Object.keys(RUN_POWERS).filter(
    (id) => (runPowers[id] || 0) < RUN_POWERS[id].maxLevel
  );
}

/**
 * @param {Object} args
 * @param {Object} args.level                  Current level data (uses level.id, level.wave, level.goal)
 * @param {Object} args.runPowers              In-run upgrade levels
 * @param {Function} args.setRunPowers         State setter
 * @param {number} args.zombiesCollected       Live (5Hz) counter from Game.jsx
 * @param {boolean} args.isVip                 Whether the user gets 4 choices + 1 reroll
 * @param {Object} args.endedRef               { current: bool } — short-circuits side effects after run end
 * @param {Object} args.user                   AuthContext user (used for lucky_run_pending)
 * @param {Object} args.talentBonuses          Output of computeTalentBonuses (uses luckyRunBonus)
 */
export function useUpgradeChooser({
  level,
  runPowers,
  setRunPowers,
  zombiesCollected,
  isVip,
  endedRef,
  user,
  talentBonuses,
}) {
  const [chooserOptions, setChooserOptions] = useState(null);
  const rewardMeterRef = useRef({ count: 0, nextAt: 0, baseStep: 0 });

  // Reset the per-level reward meter so the first popup of each new wave
  // fires at ~20% of the new goal. RunPowers persist across waves (handled
  // by App.js) and are intentionally NOT reset here.
  useEffect(() => {
    const baseStep = Math.max(2, Math.round(level.goal * 0.20));
    rewardMeterRef.current = { count: 0, nextAt: baseStep, baseStep };
    setChooserOptions(null);
  }, [level.id, level.wave, level.goal]);

  // Consume "Lucky Run" buff from the daily crate (if active). Grants ONE
  // random Lv 1 starter power for free at the start of this run.
  useEffect(() => {
    if (!user || !user.lucky_run_pending) return;
    let cancelled = false;
    (async () => {
      const res = await consumeLuckyRun();
      if (cancelled || !res || !res.ok) return;
      const candidates = buildPool(runPowers);
      if (!candidates.length) return;
      const pick = candidates[Math.floor(Math.random() * candidates.length)];
      const grant = 1 + (talentBonuses?.luckyRunBonus || 0);
      setRunPowers((p) => ({
        ...p,
        [pick]: Math.min(RUN_POWERS[pick].maxLevel, (p[pick] || 0) + grant),
      }));
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.lucky_run_pending]);

  // Trigger the Deaded Enemy Rewards chooser when the player has collected
  // enough zombies for the next reward threshold. The meter REPEATS — every
  // pick raises the bar for the next one (survivor-style).
  useEffect(() => {
    if (chooserOptions || endedRef.current) return;
    const m = rewardMeterRef.current;
    if (!m.nextAt || zombiesCollected < m.nextAt) return;
    const pool = buildPool(runPowers);
    if (pool.length === 0) return;
    const choiceCount = isVip ? 4 : 3;
    const shuffled = [...pool].sort(() => Math.random() - 0.5).slice(0, choiceCount);
    const rarities = rollRarities(shuffled.length);
    if (rarities.includes("epic")) playEpicReveal();
    setChooserOptions({
      ids: shuffled,
      rarities,
      threshold: m.nextAt,
      pickIndex: m.count + 1,
      rerollsLeft: isVip ? 1 : 0,
    });
  }, [zombiesCollected, chooserOptions, runPowers, isVip, endedRef]);

  // VIP players can reroll the current chooser (excludes already-maxed
  // automatically because we rebuild the pool from runPowers).
  const handleReroll = useCallback(() => {
    if (!chooserOptions || (chooserOptions.rerollsLeft || 0) <= 0) return;
    playClick();
    const pool = buildPool(runPowers);
    if (pool.length === 0) return;
    const choiceCount = isVip ? 4 : 3;
    const shuffled = [...pool].sort(() => Math.random() - 0.5).slice(0, choiceCount);
    const rarities = rollRarities(shuffled.length);
    if (rarities.includes("epic")) playEpicReveal();
    setChooserOptions((prev) =>
      prev
        ? {
            ...prev,
            ids: shuffled,
            rarities,
            rerollsLeft: Math.max(0, (prev.rerollsLeft || 0) - 1),
          }
        : prev
    );
  }, [chooserOptions, runPowers, isVip]);

  const handleChoose = useCallback(
    (id) => {
      playClick();
      const opts = chooserOptions;
      // Epic-rarity picks grant +2 levels at once; others +1. Capped at maxLevel.
      let bump = 1;
      if (opts && id) {
        const idx = opts.ids.indexOf(id);
        if (idx >= 0 && opts.rarities && opts.rarities[idx] === "epic") bump = 2;
      }
      if (id) {
        setRunPowers((p) => ({
          ...p,
          [id]: Math.min(RUN_POWERS[id].maxLevel, (p[id] || 0) + bump),
        }));
      }
      const meter = rewardMeterRef.current;
      meter.count += 1;
      // Next threshold scales quadratically — early picks come close
      // together, late picks are spread far apart. waveScale further
      // stretches them in higher waves.
      const waveScale = 1 + Math.max(0, ((level.wave || 1) - 1) * 0.1);
      const n = meter.count;
      const growth = 1 + n * 0.3 + n * n * 0.04;
      const delta = Math.max(3, Math.round(meter.baseStep * growth * waveScale));
      meter.nextAt = meter.nextAt + delta;
      setChooserOptions(null);
    },
    [level.wave, setRunPowers, chooserOptions]
  );

  return { chooserOptions, handleChoose, handleReroll };
}
